// IconButton — square button for icon-only actions. Used in top bars,
// row hover menus, etc. Two sizes: md (36×36) and lg (44×44 — mobile-tap).
//
// `label` is required for accessibility (becomes aria-label).

import { forwardRef, type ReactNode } from 'react';

export interface IconButtonProps {
  /** Required for screen readers. */
  label: string;
  children: ReactNode;
  size?: 'md' | 'lg';
  variant?: 'plain' | 'bordered';
  onClick?: () => void;
  disabled?: boolean;
  className?: string;
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  function IconButton(
    {
      label,
      children,
      size = 'md',
      variant = 'plain',
      onClick,
      disabled,
      className,
    },
    ref,
  ) {
    const dim = size === 'lg' ? 'h-11 w-11' : 'h-9 w-9';
    const variantCls =
      variant === 'bordered'
        ? 'border border-tcs-border-strong bg-tcs-bg hover:bg-tcs-bg-muted'
        : 'text-tcs-text-secondary hover:bg-tcs-surface hover:text-tcs-text';
    return (
      <button
        ref={ref}
        type="button"
        aria-label={label}
        title={label}
        onClick={onClick}
        disabled={disabled}
        className={`inline-grid place-items-center rounded-lg transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-tcs-accent disabled:opacity-50 ${dim} ${variantCls} ${className ?? ''}`.trim()}
      >
        {children}
      </button>
    );
  },
);
